import { Node } from '@xyflow/react';

import { ECableType } from '../edges/types';
import { EDataType, NodeCategoryModel, EPortConnectionType } from './node-data';
import { NodeModel } from './node-model';
import { getEdgeData, getPortDataFromHandle } from './node-utils';

export function getPortConnectionTypeFromHandle(
  nodes: Node<NodeModel>[],
  handle: string,
): EPortConnectionType | undefined {
  const portData = getPortDataFromHandle(nodes, handle);
  if (portData?.connectionType) {
    return portData.connectionType;
  }
  return getEdgeData(handle)?.connectionType;
}

export function isFeGePortMismatch(
  a: EPortConnectionType | undefined,
  b: EPortConnectionType | undefined,
): boolean {
  return (
    (a === EPortConnectionType.FE && b === EPortConnectionType.GE) ||
    (a === EPortConnectionType.GE && b === EPortConnectionType.FE)
  );
}

export function isSwitchNode(node: Node<NodeModel> | undefined): boolean {
  return (node?.data?.category as NodeCategoryModel)?.type === EDataType.Switch;
}

export function isPortConnectionAllowed({
  sourceType,
  targetType,
  sourceNode,
  targetNode,
}: {
  sourceType: EPortConnectionType | undefined;
  targetType: EPortConnectionType | undefined;
  sourceNode: Node<NodeModel> | undefined;
  targetNode: Node<NodeModel> | undefined;
}): boolean {
  if (!sourceType || !targetType) return false;
  if (sourceType === targetType) return true;
  if (
    sourceType === EPortConnectionType.MIXED ||
    targetType === EPortConnectionType.MIXED
  ) {
    return true;
  }
  if (isFeGePortMismatch(sourceType, targetType)) {
    return isSwitchNode(sourceNode) && isSwitchNode(targetNode);
  }
  return false;
}

export function connectionTypeToCableConnector(
  type: EPortConnectionType,
): ECableType | undefined {
  if (type === EPortConnectionType.GE) return ECableType.X;
  if (type === EPortConnectionType.FE) return ECableType.D;
  return undefined;
}

/**
 * Normalize a mixed (one X-coded, one D-coded) connector pair to a canonical
 * order so the same physical FE<->GE cable always groups together, regardless
 * of which switch the user dragged from. Same-type pairs are returned as-is.
 */
export function normalizeCablePair(
  a: ECableType,
  b: ECableType,
): { switchConnector: ECableType; deviceConnector: ECableType } {
  const isMixed =
    (a === ECableType.X && b === ECableType.D) ||
    (a === ECableType.D && b === ECableType.X);
  if (isMixed) {
    return { switchConnector: ECableType.X, deviceConnector: ECableType.D };
  }
  return { switchConnector: a, deviceConnector: b };
}

export function buildFeGeSwitchCableType(
  sourceType: EPortConnectionType,
  targetType: EPortConnectionType,
):
  | { switchConnector: ECableType; deviceConnector: ECableType; length: number }
  | undefined {
  if (!isFeGePortMismatch(sourceType, targetType)) return undefined;
  const sourceConnector = connectionTypeToCableConnector(sourceType);
  const targetConnector = connectionTypeToCableConnector(targetType);
  if (!sourceConnector || !targetConnector) return undefined;
  // Drag direction must not affect grouping: a FE<->GE link is always X / D.
  const { switchConnector, deviceConnector } = normalizeCablePair(
    sourceConnector,
    targetConnector,
  );
  return { switchConnector, deviceConnector, length: 0 };
}
